import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import MailOutline from '@mui/icons-material/MailOutline';
import { Box, Form, Modal, Spacer, Typography, useTheme } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';

import BusinessName from '~/components/BusinessName';
import CostcoMembershipNumberInput from '~/components/CostcoMembershipNumberInput';
import Email from '~/components/Email';
import Name from '~/components/Name';
import ButtonProgressIcon from '~/components-ds/ButtonProgressIcon';
import FormSpacer from '~/components-ds/FormSpacer';
import { useAppContent, useAppDispatch, useAppSelector, useExtraInfo } from '~/hooks';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { selectIsReferral, selectIsReferralCostco } from '~/selectors/selectAppDetails';
import {
  updateBusinessName,
  updateEmail,
  updateFirstName,
  updateLastName,
  updatePartnerMembershipNumber,
} from '~/slices/extraInfo';
import { qalTrackEngagementAction, qalTrackEventAction } from '~/thunks/analytics';
import { handleError } from '~/thunks/error';
import { saveQuoteAction } from '~/thunks/saveQuoteAction';
import { analyticsConstant } from '~/types/analytics';

interface EmailQuoteForm {
  firstName: string;
  lastName: string;
  businessName: string;
  email: string;
  partnerMembershipNumber?: string;
}

interface EmailQuoteProps {
  onClose: () => void;
  isOpen: boolean;
}

enum ModalState {
  Initial = 'Initial',
  Submitted = 'Submitted',
  Busy = 'Busy',
  Success = 'Success',
}

const EmailQuoteModal = ({ onClose, isOpen = false }: EmailQuoteProps) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.quoteModal',
  });

  const { getTextContent: getSharedText } = useAppContent({
    basePath: 'shared',
  });

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isCostco = useAppSelector(selectIsReferralCostco);
  const isReferral = useAppSelector(selectIsReferral);

  const {
    firstName,
    lastName,
    email,
    businessName,
    newsAndUpdatesConsent,
    partnerMembershipNumber,
  } = useExtraInfo();

  const { control, formState, handleSubmit } = useFormWithAnalytics<EmailQuoteForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      businessName: businessName ?? '',
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      ...(isReferral && { partnerMembershipNumber: partnerMembershipNumber ?? '' }),
    },
  });
  const [modalState, setModalState] = useState<ModalState>(ModalState.Initial);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  useEffect(() => {
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'email quote - modal: open',
        eventLabel: analyticsConstant.NOT_SET,
        data: {},
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (newsAndUpdatesConsent) {
      dispatch(
        qalTrackEventAction({
          event: 'submit',
          eventAction: 'agree to receive news and updates from QBE',
          eventLabel: 'subscribe',
        })
      );
    }
  }, [dispatch, newsAndUpdatesConsent]);

  const handleSend = handleSubmit(async (formData: EmailQuoteForm | undefined) => {
    if (!formData) {
      console.error('No form data');
      return;
    }
    dispatch(updateFirstName(formData.firstName));
    dispatch(updateLastName(formData.lastName));
    dispatch(updateEmail(formData.email));
    dispatch(updateBusinessName(formData.businessName));
    dispatch(updatePartnerMembershipNumber(formData.partnerMembershipNumber));
    setModalState(ModalState.Busy);
    try {
      await dispatch(saveQuoteAction());
    } catch (e) {
      dispatch(handleError(e));
    }
    setModalState(ModalState.Success);
  });

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    dispatch(
      qalTrackEngagementAction({
        eventAction: ' modal: close',
        eventLabel: 'email quote',
        data: {},
      })
    );
    onClose();
  };

  const handleContinue = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    onClose();
  };

  const handleExit = () => {
    window.location.href = getSharedContent('qbeHome');
  };

  if (modalState === ModalState.Success) {
    return (
      <Modal
        open={isOpen}
        title={getTextContent('content.successfullySent.title')}
        iconProps={{
          muiIcon: CheckCircleOutline,
          // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          color: theme.color.icon.success,
        }}
        id="email-quote-modal-success"
        primaryBtnProps={{
          label: getSharedText('continueStepButttonLabel'),
          'data-track': 'Continue - send email',
          onClick: handleContinue,
        }}
        onClose={handleExit}
        secondaryBtnProps={{
          label: getSharedText('exitButtonLabel'),
          'data-track': 'Exit - send email',
          onClick: handleExit,
        }}
      >
        <Typography>{getJSXContent('content.successfullySent.description')}</Typography>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      title={getTextContent('content.emailForm.title')}
      iconProps={{
        muiIcon: MailOutline,
      }}
      id="email-quote-modal"
      primaryBtnProps={{
        label: getSharedText('sendEmailButtonLabel'),
        type: 'submit',
        'data-track': 'Send email',
        disabled: !formState.isValid || modalState === ModalState.Busy,
        onClick: handleSend,
        endIcon: modalState === ModalState.Busy ? <ButtonProgressIcon /> : null,
      }}
      secondaryBtnProps={{
        label: getSharedText('backButtonLabel'),
        'data-track': 'Back - Send email',
        onClick: handleClose,
        disabled: modalState === ModalState.Busy,
      }}
      onClose={handleClose}
    >
      <Form onSubmit={handleSend}>
        <Typography>{getJSXContent('content.emailForm.description')}</Typography>
        <Spacer size="xl" />

        <Box sx={{ maxWidth: '400px', margin: '0 auto' }}>
          <Name control={control} />

          <BusinessName name="businessName" control={control} showLabel={false} />
          <FormSpacer variant="input" />

          <Email name="email" control={control} />

          {isCostco && (
            <>
              <FormSpacer variant="input" />
              <CostcoMembershipNumberInput name="partnerMembershipNumber" control={control} />
            </>
          )}
        </Box>
      </Form>
    </Modal>
  );
};

export default EmailQuoteModal;
